import React, { useState } from "react";
import {
  Popover,
  Button,
  Box,
  Typography,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { searchFilterRoles } from "../../services/UserManagement";

interface RolesFilterDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: { statuses: string[] }, data: any[]) => void;
  search: string;
}

const RolesFilterDialog =({ open, anchorEl, onClose, onApply, search }: RolesFilterDialogProps)=> {
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  const statusOptions = ["Active", "Inactive"];

  const handleToggle = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleSelectAll = () => {
    setSelectedStatuses(selectedStatuses.length === statusOptions.length ? [] : statusOptions);
  };

  const handleClearAll = () => {
    setSelectedStatuses([]);
  };

  const handleApply = async () => {
    try {
      setApplying(true);
      const res = await searchFilterRoles(selectedStatuses, search);
      onApply({ statuses: selectedStatuses }, Array.isArray(res) ? res : []);
    } catch {
      onApply({ statuses: selectedStatuses }, []);
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setSelectedStatuses([]);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { borderRadius: "0.75rem", width: "450px", mt: 1 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Filter</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      
      <Divider />

      <Box sx={{ p: 0, display: "flex", height: "200px", backgroundColor: "background.default" }}>
        <Box sx={{ width: "40%", borderRight: "1px solid", borderColor: "grey.300", p: 1.5, backgroundColor: "background.default" }}>
          <Box sx={{ p: 1, borderRadius: "6px", backgroundColor: "white", color: "primary.main", fontWeight: 600 }}>
            <Typography variant="body2">Status</Typography>
          </Box>
        </Box>
        
        <Box sx={{ width: "60%", p: 1.5, backgroundColor: "white" }}>
          <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedStatuses.length === statusOptions.length}
                  indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < statusOptions.length}
                  onChange={handleSelectAll}
                  sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
                />
              }
              label={<Typography variant="body2">Select All</Typography>}
              sx={{ mb: 1 }}
            />
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ maxHeight: "180px", overflowY: "auto", "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
              <FormGroup>
                {statusOptions.map((status) => (
                  <FormControlLabel
                    key={status}
                    control={
                      <Checkbox
                        checked={selectedStatuses.includes(status)}
                        onChange={() => handleToggle(status)}
                        sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }}
                      />
                    }
                    label={<Typography variant="body2">{status}</Typography>}
                  />
                ))}
              </FormGroup>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", backgroundColor: "background.default" }}>
        <Button onClick={handleClearAll} sx={{ textTransform: "none", color: "text.primary", "&:hover": { backgroundColor: "transparent" } }}>
          Clear all
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: "6px", textTransform: "none", borderColor: "grey.300", color: "text.primary", backgroundColor: "#fff", "&:hover": { backgroundColor: "#fff", borderColor: "grey.300" } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={applying} sx={{ borderRadius: "6px", textTransform: "none", backgroundColor: "primary.main", "&:hover": { backgroundColor: "primary.main" } }}>
            {applying ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "OK"}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
export default RolesFilterDialog;
